import { useEffect, useRef, useState } from 'react'

// Animates a number from its previous value to the next whenever `value` changes, so a polled refresh
// rolls the hero figures up rather than snapping. Eases out so the count decelerates into its target.
// Respects prefers-reduced-motion by jumping straight to the value.
export function useCountUp(value: number, durationMs = 900): number {
  const [display, setDisplay] = useState(value)
  const fromRef = useRef(value)
  const frameRef = useRef<number | null>(null)

  useEffect(() => {
    const prefersReduced =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

    const from = fromRef.current
    const to = value

    if (prefersReduced || from === to) {
      fromRef.current = to
      setDisplay(to)
      return
    }

    let start: number | null = null
    const step = (timestamp: number) => {
      if (start === null) start = timestamp
      const progress = Math.min((timestamp - start) / durationMs, 1)
      // easeOutCubic
      const eased = 1 - (1 - progress) ** 3
      setDisplay(from + (to - from) * eased)
      if (progress < 1) {
        frameRef.current = requestAnimationFrame(step)
      } else {
        fromRef.current = to
      }
    }

    frameRef.current = requestAnimationFrame(step)
    return () => {
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current)
    }
  }, [value, durationMs])

  return display
}
